"use client"
import { MAX_TRUST_SCORE } from "@/lib/trust/constants"
import Link from "next/link"
import Image from "next/image"
import { useEffect, useState } from "react"
import Icon from "@/components/ui/Icon"
import { useFavourites } from "@/lib/hooks/useFavourites"
import { readStoredUser } from "@/lib/safe-storage"
import { sellerDisplayName } from "@/lib/accounts/display-name"
import SellerLink from "@/components/SellerLink"

export default function Home() {
  const [tab, setTab] = useState("Buy")
  const [searchQuery, setSearchQuery] = useState("")
  const [searchCity, setSearchCity] = useState("")
  const [searchPrice, setSearchPrice] = useState("")
  const [searchBeds, setSearchBeds] = useState("")
  const [featuredListings, setFeaturedListings] = useState<any[]>([])
  const [spotlights, setSpotlights] = useState<any[]>([])
  const [featuredLoading, setFeaturedLoading] = useState(true)
  const [slideIndex, setSlideIndex] = useState(0)
  const [homePosts, setHomePosts] = useState<any[]>([])
  const [homeSuburbs, setHomeSuburbs] = useState<any[]>([])
  const [recentSales, setRecentSales] = useState<any[]>([])
  const { favs, toggle: toggleFavDB } = useFavourites()
  const [loggedIn, setLoggedIn] = useState(false)

  useEffect(() => {
    fetch("/api/spotlights").then(r => r.json()).then(d => setSpotlights(d.data ?? [])).catch(() => {})
    // isFeatured is set ONLY by the paid boost / monthly-credit path
    // (api/listings/[id]/feature). Credits require an ACTIVE subscription, and
    // during Free Trial Mode nobody subscribes — so featured=true returns an
    // empty set for the whole trial and the homepage's largest rail renders
    // blank even once real listings exist. Fall back to the newest live
    // listings so the slot is never empty for want of a paid boost.
    fetch("/api/listings?featured=true&pageSize=12")
      .then(r => r.json())
      .then(d => {
        const featured = d.data ?? []
        if (featured.length > 0) { setFeaturedListings(featured); setFeaturedLoading(false); return }
        return fetch("/api/listings?pageSize=12&sort=newest")
          .then(r => r.json())
          .then(f => { setFeaturedListings(f.data ?? []); setFeaturedLoading(false) })
      })
      .catch(() => setFeaturedLoading(false))
    fetch("/api/blog?limit=3").then(r => r.json()).then(d => { setHomePosts((d.data ?? []).slice(0, 3)) }).catch(() => {})
    fetch("/api/suburbs").then(r => r.json()).then(d => { setHomeSuburbs((d.data ?? []).slice(0, 5)) }).catch(() => {})
    fetch("/api/sold-prices?city=Harare&limit=5").then(r => r.json()).then(d => { setRecentSales(d.data ?? []) }).catch(() => {})
    setLoggedIn(!!readStoredUser().id)
  }, [])

  // ── Hero parallax (dot grid and colour blobs drift on scroll)
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      if (y > window.innerHeight) return
      const dots = document.getElementById("hero-dots")
      const b1 = document.getElementById("hero-b1")
      const b2 = document.getElementById("hero-b2")
      const b3 = document.getElementById("hero-b3")
      if (dots) dots.style.transform = `translateY(${y * 0.32}px)`
      if (b1) b1.style.transform = `translateY(${y * -0.14}px)`
      if (b2) b2.style.transform = `translateY(${y * 0.18}px)`
      if (b3) b3.style.transform = `translateY(${y * -0.1}px)`
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  function toggleFav(id: string, e: React.MouseEvent) { toggleFavDB(id, e) }

  const [slidePaused, setSlidePaused] = useState(false)
  useEffect(() => {
    if (featuredListings.length <= 1 || slidePaused) return
    // WCAG 2.2.2: anything that auto-advances for more than five seconds needs a
    // pause control, and a reduced-motion preference has to be honoured. This
    // carousel moved forever with neither.
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return
    const t = setInterval(() => setSlideIndex(i => (i + 1) % featuredListings.length), 5000)
    return () => clearInterval(t)
  }, [featuredListings.length, slidePaused])

  return (
    <>
      <section className="hero" id="ze-hero">
        {/* Parallax dot grid, separate element so we can transform it independently */}
        <div id="hero-dots" style={{ position: "absolute", inset: "-20%", backgroundImage: "radial-gradient(circle, rgba(27,45,84,0.07) 1.5px, transparent 1.5px)", backgroundSize: "28px 28px", pointerEvents: "none", willChange: "transform", zIndex: 0 }} />
        {/* Colour blobs */}
        <div id="hero-b1" className="hero-blob hero-blob-1" />
        <div id="hero-b2" className="hero-blob hero-blob-2" />
        <div id="hero-b3" className="hero-blob hero-blob-3" />

        <div className="hero-inner" style={{ position: "relative", zIndex: 1 }}>
          <div>
            <span className="eyebrow hero-animate-1">Verified Listings · Direct Connection</span>
            <h1 className="hero-animate-2">Find a home you can <em>trust</em>.</h1>
            <p className="hero-sub hero-animate-3">
              Every listing verified. Every agent reviewed. Connect directly with sellers via WhatsApp or call. No middlemen, no hidden fees.
            </p>

            <div className="search-card hero-animate-4">
              <div className="search-tabs">
                {["Buy", "Rent", "Land", "Commercial"].map(t => (
                  <button key={t} onClick={() => setTab(t)} className={"search-tab" + (tab === t ? " active" : "")}>{t}</button>
                ))}
              </div>
              <div className="search-fields">
                <div className="search-field">
                  <label>City</label>
                  <select value={searchCity} onChange={e => setSearchCity(e.target.value)} aria-label="City">
                    <option value="">Any city</option>
                    <option value="Harare">Harare</option>
                    <option value="Bulawayo">Bulawayo</option>
                    <option value="Mutare">Mutare</option>
                    <option value="Gweru">Gweru</option>
                    <option value="Victoria Falls">Victoria Falls</option>
                    <option value="Masvingo">Masvingo</option>
                    <option value="Kwekwe">Kwekwe</option>
                    <option value="Chinhoyi">Chinhoyi</option>
                    <option value="Bindura">Bindura</option>
                  </select>
                </div>
                <div className="search-field">
                  <label>Area / suburb</label>
                  <input placeholder="e.g. Borrowdale, Avondale" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                </div>
                <div className="search-field">
                  <label>Max price (USD)</label>
                  <input placeholder="Any price" value={searchPrice} onChange={e => setSearchPrice(e.target.value)} type="number" />
                </div>
                <div className="search-field">
                  <label>Beds</label>
                  <select value={searchBeds} onChange={e => setSearchBeds(e.target.value)} aria-label="Minimum bedrooms">
                    <option value="">Any</option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                  </select>
                </div>
              </div>
              <Link href={"/listings?" + new URLSearchParams(Object.fromEntries(Object.entries({ priceType: tab === "Rent" ? "RENT" : "", propertyType: tab === "Land" ? "LAND" : tab === "Commercial" ? "COMMERCIAL" : "", city: searchCity.trim(), suburb: searchQuery.trim(), maxPrice: searchPrice.trim(), beds: searchBeds }).filter(([, v]) => v))).toString()} className="search-btn" style={{ display: "block", textAlign: "center" }}>
                Search listings {'>'}
              </Link>
            </div>

          </div>

          <div className="hero-animate-right">
            {featuredLoading ? (
              <div className="hero-feature" style={{ height: 400, background: "var(--cream-warm)" }} />
            ) : featuredListings.length === 0 ? (
              <div className="hero-feature" style={{ padding: 40, textAlign: "center" }}>
                <div style={{ color: "rgba(253,251,247,0.6)", fontSize: 13 }}>No listings yet.</div>
              </div>
            ) : (
              <div className="hero-feature">
                <Link href="/listings?featured=true" style={{ display: "block", textDecoration: "none", overflow: "hidden" }}>
                  <div style={{ display: "flex", transform: `translateX(-${slideIndex * 100}%)`, transition: "transform 0.55s cubic-bezier(0.4,0,0.2,1)", willChange: "transform" }}>
                    {featuredListings.map(l => {
                      const photo = l.photos?.[0]?.url
                      const fallbackBg = l.priceType === "RENT" ? "linear-gradient(135deg,#3d8b5f,#1B2D54)" : "linear-gradient(135deg,#3a4f7a,#1B2D54)"
                      return (
                        <div key={l.id} style={{ minWidth: "100%", padding: 24, boxSizing: "border-box" }}>
                          <div style={{ height: 280, background: photo ? undefined : fallbackBg, borderRadius: 12, marginBottom: 16, position: "relative", overflow: "hidden" }}>
                            {photo && <Image src={photo} alt={l.title} fill sizes="(max-width: 640px) 100vw, 600px" style={{ objectFit: "cover" }} />}
                            {/* Only badge a listing that is genuinely on a paid/credit
                                boost. The rail falls back to newest listings when
                                nothing is featured, and an unconditional badge would
                                label ordinary listings as promoted. */}
                            {l.isFeatured && <span style={{ position: "absolute", top: 12, left: 12, background: "#C8963E", color: "#1B2D54", fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 6, textTransform: "uppercase", zIndex: 2 }}>Featured</span>}
                            {l.trustScore && <span style={{ position: "absolute", top: 12, right: 12, background: "rgba(253,251,247,0.94)", color: "var(--green-text)", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6, zIndex: 2, boxShadow: "0 1px 2px rgba(14,22,42,0.25)" }}>Trust {l.trustScore.totalScore}/{MAX_TRUST_SCORE}</span>}
                            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "56px 16px 14px", background: "linear-gradient(to top, rgba(14,22,42,0.95) 0%, transparent 100%)", zIndex: 1 }}>
                              <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: "#FDFBF7", lineHeight: 1.3 }}>{l.title}</div>
                              <div style={{ fontSize: 12, color: "rgba(253,251,247,0.65)", marginTop: 3 }}>{l.suburb}, {l.city}</div>
                            </div>
                          </div>
                          <div style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 700, color: "#FDFBF7", marginBottom: 8 }}>
                            ${l.price?.toLocaleString()} <span style={{ fontSize: 14, fontWeight: 400, color: "rgba(253,251,247,0.75)" }}>{l.priceType === "RENT" ? "/mo" : "USD"}</span>
                          </div>
                          <div style={{ display: "flex", gap: 14, fontSize: 12, color: "rgba(253,251,247,0.78)" }}>
                            {l.beds > 0 && <span>{l.beds} beds</span>}
                            {l.baths > 0 && <span>{l.baths} baths</span>}
                            {l.floorArea > 0 && <span>{l.floorArea}m²</span>}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </Link>
                {featuredListings.length > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, padding: "0 24px 18px" }}>
                    {/* Nameless, stateless buttons: a screen reader read out N
                        identical "button"s and never said which was current. */}
                    <div role="tablist" aria-label="Choose featured property" style={{ display: "flex", gap: 6 }}>
                      {featuredListings.map((l, i) => (
                        <button
                          key={i}
                          role="tab"
                          aria-label={`Featured property ${i + 1} of ${featuredListings.length}${l?.title ? ": " + l.title : ""}`}
                          aria-selected={i === slideIndex}
                          onClick={() => { setSlideIndex(i); setSlidePaused(true) }}
                          style={{ width: i === slideIndex ? 22 : 6, height: 6, borderRadius: 3, background: i === slideIndex ? "#C8963E" : "rgba(253,251,247,0.22)", border: "none", cursor: "pointer", transition: "all 0.35s", padding: 0 }}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => setSlidePaused(p => !p)}
                      aria-pressed={slidePaused}
                      style={{ marginLeft: 10, background: "none", border: "none", color: "rgba(253,251,247,0.78)", fontSize: 11, fontWeight: 600, cursor: "pointer", padding: "2px 6px", fontFamily: "inherit" }}
                    >
                      {slidePaused ? "Play" : "Pause"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>{/* /hero-inner */}
      </section>

      {!loggedIn && (
        <div className="section" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <div className="reveal-scale" style={{ background: "var(--navy)", borderRadius: 20, padding: "28px 36px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(200,150,62,0.07)", pointerEvents: "none" }} />
            <div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "var(--cream)", marginBottom: 6 }}>Join Zim Estates free</div>
              <div style={{ fontSize: 14, color: "rgba(253,251,247,0.65)", maxWidth: 420 }}>Save listings, get price alerts, and contact verified agents directly. No middlemen.</div>
            </div>
            <div style={{ display: "flex", gap: 10, flexShrink: 0, position: "relative" }}>
              <Link href="/auth/register" style={{ padding: "12px 28px", background: "var(--gold)", color: "var(--navy)", borderRadius: 10, fontSize: 14, fontWeight: 700, textDecoration: "none", transition: "background 0.15s, transform 0.1s" }}>Create account</Link>
              <Link href="/auth/login" style={{ padding: "12px 24px", background: "rgba(253,251,247,0.1)", color: "var(--cream)", border: "1px solid rgba(253,251,247,0.2)", borderRadius: 10, fontSize: 14, fontWeight: 600, textDecoration: "none" }}>Log in</Link>
            </div>
          </div>
        </div>
      )}

      <div className="section">
        <div className="section-head reveal">
          <div>
            <h2 className="section-title">Tools that <em>help you decide</em>.</h2>
            <p className="section-sub">Built for the way Zimbabweans actually buy and rent property.</p>
          </div>
        </div>
        <div className="tools-strip reveal">
          {[
            { icon: "Heart", label: "Save favourites", desc: "Heart any listing and keep them in one place.", href: "/favourites", color: "#c44444" },
            { icon: "Compare", label: "Compare side by side", desc: "Up to three listings: specs, price and features.", href: "/compare", color: "#a87a2c" },
            { icon: "Location", label: "Browse on the map", desc: "See exactly where every property sits.", href: "/map", color: "#3d8b5f" },
            { icon: "Stats", label: "Suburb price stats", desc: "Listings and price stats, suburb by suburb.", href: "/suburbs", color: "#1B2D54" },
          ].map(tool => (
            <Link key={tool.href} href={tool.href} className="tool-cell">
              <div className="tool-icon" style={{ color: tool.color }}><Icon name={tool.icon} size={20} /></div>
              <div>
                <div className="tool-cell-title">{tool.label}</div>
                <div className="tool-cell-desc">{tool.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="section" style={{ paddingTop: 0 }}>
        <div className="section-head reveal">
          <div>
            <h2 className="section-title">Browse by <em>suburb</em></h2>
            <p className="section-sub">Pick a suburb to see its listings.</p>
          </div>
          <Link href="/suburbs" className="btn-outline">View all suburbs {'>'}</Link>
        </div>
        {homeSuburbs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 24px", background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16 }}>
            <p style={{ color: "var(--muted)", marginBottom: 16 }}>Suburb data is loading, check back shortly.</p>
            <Link href="/suburbs" className="btn-outline">Browse all suburbs</Link>
          </div>
        ) : (
          <div className="suburbs-grid">
            {homeSuburbs.map((suburb, i) => {
              const bgs = ["linear-gradient(135deg,#2a3f6e,#1B2D54)","linear-gradient(135deg,#a87a2c,#C8963E)","linear-gradient(135deg,#3d8b5f,#1B2D54)","linear-gradient(135deg,#142142,#2a3f6e)","linear-gradient(135deg,#C8963E,#a87a2c)"]
              const count = suburb.activeListings > 0
                ? `${suburb.activeListings} listing${suburb.activeListings !== 1 ? "s" : ""}`
                : "View listings"
              return (
                // Links to the suburb page (price stats + its listings, as the
                // subtitle promises). It used to search /listings with the SLUG
                // ("gweru-daylesford"), which matches no suburb name, so a tile
                // saying "3 listings" opened an empty results page.
                <Link key={suburb.slug ?? suburb.name} href={suburb.slug ? "/suburbs/" + suburb.slug : "/listings?suburb=" + encodeURIComponent(suburb.name.trim())} className="suburb-card reveal" style={{ transitionDelay: `${i * 0.07}s` }}>
                  <div className="suburb-bg" style={{ background: bgs[i % bgs.length] }}>
                    {/* The API has returned a photo per suburb (admin cover, else a
                        live listing photo) but this tile only ever drew a gradient.
                        The gradient stays as the fallback and behind the image. */}
                    {suburb.imageUrl && (
                      <Image src={suburb.imageUrl} alt={suburb.name.trim()} fill sizes={i === 0 ? "(max-width: 900px) 100vw, 50vw" : "(max-width: 900px) 100vw, 25vw"} priority={i === 0} style={{ objectFit: "cover" }} />
                    )}
                  </div>
                  <div className="suburb-overlay"></div>
                  <div className="suburb-content">
                    <div className="suburb-name">{suburb.name.trim()}</div>
                    <div className="suburb-count">{count}</div>
                  </div>
                  <span className="suburb-pill">View listings</span>
                </Link>
              )
            })}
          </div>
        )}
      </div>

      {/* SPOTLIGHT — paid 24h slots, clearly labelled. Separate from organic
          results and the featured rail; never affects search ranking. */}
      {spotlights.length > 0 && (
        <div className="section" style={{ paddingTop: 0, paddingBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 600, color: "var(--navy)", margin: 0 }}>Spotlight</h2>
            <span style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", border: "1px solid var(--line)", borderRadius: 4, padding: "2px 6px" }}>Sponsored</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
            {spotlights.map((s: any) => (
              <Link key={s.spotlightId} href={"/listings/" + s.id} style={{ display: "block", background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden", textDecoration: "none" }}>
                <div style={{ height: 120, background: s.photos?.[0]?.url ? `url(${s.photos[0].url}) center/cover` : "linear-gradient(135deg,#3a4f7a,#1B2D54)" }} />
                <div style={{ padding: "10px 12px" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>{s.suburb}, {s.city}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--gold-deep)", marginTop: 2 }}>${Number(s.price).toLocaleString()}{s.priceType === "RENT" ? "/mo" : ""}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="section" style={{ paddingTop: 0 }}>
        <div className="section-head reveal">
          <div>
            <h2 className="section-title">{featuredListings.some(l => l.isFeatured) ? <>Featured <em>listings</em></> : <>Latest <em>listings</em></>}</h2>
            <p className="section-sub">{featuredListings.some(l => l.isFeatured) ? "Promoted listings from verified sellers." : "The newest listings, checked by our team before going live."}</p>
          </div>
          <Link href="/listings" className="btn-outline">See all listings {'>'}</Link>
        </div>
        {featuredLoading ? (
          <div className="listings-grid">
            {[0, 1, 2].map(i => <div key={i} style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, height: 380, opacity: 0.5 }} />)}
          </div>
        ) : featuredListings.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 24px", background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16 }}>
            <p style={{ color: "var(--muted)", marginBottom: 12 }}>No featured listings yet.</p>
            <Link href="/listings" style={{ fontSize: 13, color: "var(--gold-deep)", fontWeight: 600, textDecoration: "none" }}>Browse all listings &rsaquo;</Link>
          </div>
        ) : (
          <div className="listings-grid">
            {featuredListings.map(listing => {
              const photo = listing.photos?.[0]?.url
              const fallbackBg = listing.priceType === "RENT" ? "linear-gradient(135deg,#3d8b5f,#1B2D54)" : "linear-gradient(135deg,#3a4f7a,#1B2D54)"
              const agentInitials = sellerDisplayName(listing.agent, "ZE").substring(0, 2).toUpperCase()
              return (
                <Link key={listing.id} href={"/listings/" + listing.id} className="listing-card">
                  <div className="listing-img" style={{ background: photo ? undefined : fallbackBg }}>
                    {photo && <Image src={photo} alt={listing.title} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" style={{ objectFit: "cover" }} />}
                    {listing.isFeatured && <span className="listing-tag gold">Featured</span>}
                    <div className="listing-actions">
                      {/* Counted server-side redirect. Public browse strips
                          agent.phone, so the old raw wa.me link never rendered
                          at all on the homepage. */}
                      <a className="listing-action-btn" href={"/api/go/whatsapp?listing=" + encodeURIComponent(listing.id)} target="_blank" rel="noopener noreferrer" aria-label={"Contact the seller about " + listing.title + " on WhatsApp"} onClick={e => e.stopPropagation()}><Icon name="Message" size={15} /></a>
                      <button className="listing-action-btn" onClick={e => toggleFav(listing.id, e)} style={{ color: favs.includes(listing.id) ? "#c44444" : undefined }}>
                        {favs.includes(listing.id) ? "♥" : "♡"}
                      </button>
                    </div>
                  </div>
                  <div className="listing-body">
                    <div className="listing-price">${listing.price?.toLocaleString()} <em>{listing.priceType === "RENT" ? "/mo" : "USD"}</em></div>
                    <div className="listing-title">{listing.title}</div>
                    <div className="listing-loc">{listing.suburb}, {listing.city}</div>
                    <div className="listing-meta">
                      {listing.beds > 0 && <span>{listing.beds} beds</span>}
                      {listing.baths > 0 && <span>{listing.baths} baths</span>}
                      {listing.floorArea > 0 && <span>{listing.floorArea}m²</span>}
                    </div>
                    {listing.trustScore && <div className="trust-score">Trust Score {listing.trustScore.totalScore}/{MAX_TRUST_SCORE}</div>}
                    <div className="listing-agent">
                      <div className="agent-avatar">{agentInitials}</div>
                      <div>
                        <SellerLink agent={listing.agent} nested className="agent-name" style={{ textDecoration: "underline", textDecorationColor: "var(--line)", textUnderlineOffset: 3 }}>{sellerDisplayName(listing.agent, "Zim Estates")}</SellerLink>
                        {/* This badge was unconditional — every seller was stamped
                            "Verified Agent" whether or not they had passed KYC.
                            trustScore.idVerified IS the KYC-approved check. */}
                        {listing.trustScore?.idVerified && <div className="agent-badge">✓ Verified Agent</div>}
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>

      {recentSales.length > 0 && (
        <div className="section" style={{ paddingTop: 0 }}>
          <div className="section-head reveal">
            <div>
              <h2 className="section-title">Recent sales in <em>Harare</em></h2>
              <p className="section-sub">Agent-reported sold prices. Updated as sales are recorded.</p>
              <p style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>Agent-reported prices · Independently reviewed · Not all sales captured</p>
            </div>
            <Link href="/suburbs" className="btn-outline">Suburb stats {'>'}</Link>
          </div>
          <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, overflow: "hidden" }}>
            {recentSales.map((sp: any, i: number) => (
              <div key={sp.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: i < recentSales.length - 1 ? "1px solid var(--line)" : "none", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{sp.address}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    {sp.suburb?.name ?? "Harare"}
                    {sp.beds ? (" · " + sp.beds + " bed") : ""}
                    {sp.floorArea ? (" · " + sp.floorArea + "m²") : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: "var(--navy)" }}>${sp.soldPrice.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>{new Date(sp.soldDate).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="section" style={{ paddingTop: 0 }}>
        <div className="trust-section reveal-scale">
          <div className="trust-inner">
            <span className="eyebrow">The Trust Difference</span>
            <h2 className="section-title" style={{ color: "var(--cream)", marginTop: 16, maxWidth: 700 }}>
              Property scams cost Zimbabweans millions every year. We are ending that.
            </h2>
            <div className="trust-grid">
              {[
                { icon: "Verified", title: "Trust Score on every listing", desc: "Every listing carries a Trust Score out of 90: ID and selfie checked by a person, phone verified, title deed checked, photos reviewed, EAC licence on file." },
                { icon: "Photo", title: "Photos and ID checked by a person", desc: "A member of our team looks at the ID, the selfie and the photos before a listing goes live, and buyers can report a listing that reuses photos from elsewhere." },
                { icon: "Alert", title: "One-tap fraud reporting", desc: "Spot something off? Report any listing in 30 seconds. Our team investigates within 48 hours." },
              ].map((card, i) => (
                <div key={card.title} className="trust-card reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                  <div className="trust-icon"><Icon name={card.icon} size={24} /></div>
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center", marginTop: 40 }}>
              <Link href="/trust-centre" className="btn-primary">Visit the Trust Centre {'>'}</Link>
            </div>
          </div>
        </div>
      </div>

      {homePosts.length > 0 && (
        <div className="section" style={{ paddingTop: 0 }}>
          <div className="section-head reveal">
            <div>
              <h2 className="section-title">Latest from the <em>blog</em></h2>
              <p className="section-sub">Property insights, market updates, and fraud alerts.</p>
            </div>
            <Link href="/blog" className="btn-outline">All articles {'>'}</Link>
          </div>
          <div className="blog-grid">
            {homePosts.map((post, i) => {
              const catBgs: Record<string, string> = { alert: "linear-gradient(135deg,#c44,#142142)", guide: "linear-gradient(135deg,#3d8b5f,#1B2D54)", market: "linear-gradient(135deg,#C8963E,#a87a2c)", news: "linear-gradient(135deg,#2a3f6e,#1B2D54)", legal: "linear-gradient(135deg,#142142,#3a4f7a)", investment: "linear-gradient(135deg,#a87a2c,#3d8b5f)" }
              const bg = catBgs[post.category] ?? catBgs.market
              const catLabel = (post.category ?? "post").charAt(0).toUpperCase() + (post.category ?? "post").slice(1)
              const excerpt = post.excerpt || post.metaDescription || (post.body ? post.body.substring(0, 140) + "..." : "")
              const readTime = post.body ? `${Math.max(1, Math.ceil(post.body.split(" ").length / 200))} min read` : ""
              return (
                <Link key={i} href={post.slug ? "/blog/" + post.slug : "/blog"} className="blog-card reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                  <div className="blog-card-img" style={post.coverImage ? { backgroundImage: `url(${post.coverImage})` } : { background: bg }} />
                  <div className="blog-card-body">
                    <span className={"blog-cat " + post.category}>{catLabel}</span>
                    <h3>{post.title}</h3>
                    <p className="blog-excerpt">{excerpt}</p>
                    <div className="blog-meta"><span>{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>{readTime && <><span>·</span><span>{readTime}</span></>}</div>
                    <div className="blog-read-more">Read article <span>&rsaquo;</span></div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}